import type { Passage } from "./types";

export const passage: Passage = {
  slug: "jeremiah-29-11",
  reference: "Jeremiah 29:11",
  title: "Jeremiah 29:11 meaning: the letter it comes from",
  h1: "Jeremiah 29:11, and the letter it comes from",
  description:
    "The plans-to-prosper-you verse is a line from a letter to deported exiles about seventy years. What the surrounding paragraph says, and why it changes the promise.",
  targets: [
    "jeremiah 29:11 meaning",
    "for i know the plans i have for you context",
    "is jeremiah 29 11 taken out of context",
  ],
  published: "2026-08-27",
  standfirst:
    "It appears on graduation cards. It is the eleventh verse of a letter telling deported people to build houses in the country that deported them.",
  verse: {
    text: "For I know the thoughts that I think toward you, says Yahweh, thoughts of peace, and not of evil, to give you hope and a future.",
    ref: "Jeremiah 29:11",
  },
  context: [
    {
      text: "Build houses and dwell in them. Plant gardens and eat their fruit … Seek the peace of the city where I have caused you to be carried away captive, and pray to Yahweh for it; for in its peace you will have peace.",
      ref: "Jeremiah 29:5, 7",
    },
    {
      text: "For Yahweh says, 'After seventy years are accomplished for Babylon, I will visit you and perform my good word toward you, in causing you to return to this place.'",
      ref: "Jeremiah 29:10",
    },
  ],
  takeaways: [
    "It is verse eleven of a letter, and verse ten gives the timescale: seventy years. Most of the people who first read it did not live to see the promise kept.",
    "The letter's instruction is to settle down where they are — build houses, plant gardens, seek the good of the city that deported them — not to wait for rescue.",
    "The 'you' is plural and national. The promise is to a people over generations, which is a different claim from a guarantee about one person's career.",
    "None of that makes it a lesser verse. It makes it a verse that can be said to somebody whose situation is not going to improve this year.",
  ],
  body: [
    {
      type: "h2",
      id: "the-letter",
      text: "It is a line from a letter",
    },
    {
      type: "p",
      text: "Jeremiah 29 opens by saying exactly what it is: the words of a letter sent from Jerusalem to the elders, priests, prophets and people carried away captive to Babylon. The audience is a deported population living in the empire that deported them, roughly 597 BC, and the letter exists because other prophets were telling them the exile would be short and they should not unpack.",
    },
    {
      type: "p",
      text: "The letter's advice is the opposite. Build houses. Plant gardens. Marry, have children, let your children marry. Seek the peace of the city where you are captive and pray for it, because your own peace is bound up with its peace. This is an instruction to put down roots in the place you did not choose, and it is deeply unwelcome advice — which is why the letter needed to be sent at all.",
    },
    {
      type: "h2",
      id: "verse-ten",
      text: "Verse ten is the one nobody quotes",
    },
    {
      type: "p",
      text: "Immediately before the famous sentence comes the timescale, and it is the single most important thing on this page: after seventy years are accomplished for Babylon. Seventy years is not a season of waiting. For an adult reading that letter it is the rest of their life, and the promise of return is a promise to their grandchildren.",
    },
    {
      type: "p",
      text: "That is the setting in which God says he knows the thoughts he thinks toward them — thoughts of peace and not of evil, to give them hope and a future. The hope is real and it is long. The verse is not describing an imminent improvement; it is describing what is true about the one making the plans while nothing improves.",
    },
    {
      type: "h2",
      id: "who-is-you",
      text: "Who “you” is",
    },
    {
      type: "p",
      text: "Plural, throughout, and addressed to a nation by name. English flattens this — our you is the same word for one person and for a crowd — and a great deal of the popular reading depends on the flattening. Read as spoken to a people over generations, the promise is coherent with the seventy years. Read as spoken to an individual about a decision they are making this month, it is not, and the individual eventually notices.",
    },
  ],
  misreading: {
    claim:
      "That it is a personal promise of prosperity and a good outcome — the verse you give somebody who is choosing a career, starting a marriage, or hoping a hard season ends soon.",
    correction: [
      "The problem with that reading is not that it is too optimistic. It is that it promises the wrong thing on the wrong timescale, and it therefore fails precisely when it is needed most. Someone handed this verse at a graduation and again at a funeral will, at some point, notice that it did not appear to hold — and what breaks is not their view of their circumstances but their view of whether the text can be trusted.",
      "What the letter actually offers is sturdier. It says the situation is not an accident and not abandonment; that the one who allowed it has intentions of peace rather than harm; and that those intentions run on a timescale longer than a lifetime. Alongside that it gives a practical instruction — settle here, work here, pray for the good of this place — which is the only advice in the passage anyone is asked to act on.",
      "That combination is unusually well suited to the situations the verse is usually deployed in and usually mishandles. The person in a job they cannot leave, the illness that is not resolving, the family that has not spoken in years: to them the greeting-card reading says wait, it will get better soon, which may be false. The letter says build a house here, and know who is on the other side of it. That is a promise capable of being kept.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Quote verse 10 with it, or do not quote it. The seventy years is not a footnote to the promise, it is the thing that makes the promise mean something, and a page or a card that removes it has changed what is being said. That is the whole argument of {the-piece-on-reading-whole-units} — the passages that get remembered wrongly are always the ones that circulate as fragments.",
      links: {
        "the-piece-on-reading-whole-units":
          "/blog/bible-in-a-year-on-your-phone",
      },
    },
    {
      type: "p",
      text: "The other half is the instruction. Verse 5 and verse 7 are the actionable part of the letter and almost nobody applies them: build something where you are, and pray for the good of the place you did not choose. For most people that is a more demanding reading of Jeremiah 29 than the comforting one, and it is considerably more use.",
    },
    {
      type: "note",
      text: "Read the whole chapter once. It takes about four minutes and it is very difficult to hear the verse the same way afterwards.",
    },
  ],
  faq: [
    {
      q: "Is Jeremiah 29:11 taken out of context?",
      a: "Usually, yes — not by being quoted inaccurately but by being separated from verse 10, which sets the timescale at seventy years, and from the letter's instruction to settle in Babylon. The promise is to a deported nation over generations rather than to an individual about the near future.",
    },
    {
      q: "Does Jeremiah 29:11 apply to me?",
      a: "Christian traditions differ on how a promise given to Israel in exile extends to individual believers, and this page does not settle that. What can be said either way is that the promise is about the intentions of the one making the plans, on a long timescale, toward people whose situation was not about to improve.",
    },
    {
      q: "What does 'a future and a hope' mean?",
      a: "The Hebrew phrase behind it carries the sense of an outcome or an end that is coming — a latter end, in older translations. It points at where things are going rather than describing the next chapter, which fits verse 10's seventy years rather than the graduation-card reading.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain. Its rendering of verse 11 is close to the familiar one; the difference that matters is verse 10, and every translation has it.",
    },
  ],
  related: [
    "bible-in-a-year-on-your-phone",
    "how-to-memorize-bible-verses",
    "digital-sabbath",
  ],
  relatedTopics: ["bible-verses-about-time", "bible-verses-about-rest"],
  relatedPassages: ["isaiah-41-10", "proverbs-3-5-6"],
};
